import { useEffect, useState } from "react";
import { ActivityLogActiveIcon, FilterIcon2 } from "../../../../../assets/icons";
import Button from "../../../../../common/components/xepp/button/Button";
import SearchableMultiselectDropdown from "../../../../../common/components/xepp/searchable-multiselect-dropdown/SearchableMultiselectDropdown";
import Filter from "../../../../../common/components/xepp/table-filter/table-filter-components";
import DataTable, { ColumnType } from "../../../../../common/components/xepp/table/DataTable";
import { useLazyGetCustomerForUtilityAdminQuery } from "../../../../../service/customer-service";
import styles from "./LogSearchTable.module.scss";
import { FilterItem } from "../../../../../common/components/xepp/table-filter/table-filter.type";
import { formatTimestamp } from "../../../../../utils";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { Range } from "react-date-range";
import EmptyState from "../../../../../common/components/xepp/empty-state/EmptyState";
import { TablePagination } from "../../../../../common/components/xepp/table-pagination/TablePagination";
import { StaffVerifyParams, useLazyGetStaffVerifyQuery } from "../../../../../service/customersupport-service";
import { StaffVerifyResponse } from "../../../../../model/safety/staff-verify";
import SearchInput from "../../../../../common/components/xepp/search-input/SearchInput";

const LogSearchTable = () => {
	const [checkedIds, setCheckedIds] = useState<string[]>([]);
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);

	const [getStaffVerifyQuery, { data, isLoading, isFetching }] = useLazyGetStaffVerifyQuery();

	const [Page, setPage] = useState(1);
	const PageSize = 15;

	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);

	const [selectedReason, setSelectedReason] = useState<string>("");

	const [dateRange, setDateRange] = useState<Range>({
		startDate: startDate,
		endDate: now,
		key: "selection",
	});
	const [appliedFilters, setAppliedFilters] = useState<{
		PaymentStatus?: string;
		Status?: string;
		DateFrom?: string;
		DateTo?: string;
		CustomerIds?: string[];
	}>({
		PaymentStatus: "",
		Status: "",
		DateFrom: undefined,
		DateTo: undefined,
		CustomerIds: undefined,
	});

	const handleOpenFilter = () => {
		setIsFilterOpen(true);
	};

	const handleCloseFilter = () => {
		setIsFilterOpen(false);
	};

	const handleApplyFilter = () => {
		setAppliedFilters({
			...appliedFilters,
			DateFrom: dateRange.startDate ? dateRange.startDate.toISOString() : undefined,
			DateTo: dateRange.endDate ? dateRange.endDate.toISOString() : undefined,
		});

		setIsFilterOpen(false);
		setPage(1);
	};

	const handleResetFilter = () => {
		setIsFilterOpen(false);
		setCheckedIds([]);

		setDateRange({
			startDate: startDate,
			endDate: now,
			key: "selection",
		});

		setAppliedFilters({
			PaymentStatus: undefined,
			Status: undefined,
			DateFrom: startDate.toISOString(),
			DateTo: now.toISOString(),
			CustomerIds: undefined,
		});

		setPage(1);
	};

	const handleDateRangeChange = (ranges: any) => {
		setDateRange(ranges.selection);
	};

	const filterItems: FilterItem[] = [
		{
			id: "date_range",
			label: "DATE RANGE",
			type: "date",
			initialDateRange: dateRange,
			onChange: handleDateRangeChange,
		},
		{
			id: "reason_for_visit",
			label: "REASON FOR VISIT",
			type: "radio",
			options: [
				{
					id: "inspection",
					label: "Inspection",
					value: "Inspection",
					isSelected: selectedReason === "Inspection",
				},
				{
					id: "meter_reading",
					label: "Meter Reading",
					value: "MeterReading",
					isSelected: selectedReason === "MeterReading",
				},
				{
					id: "routine_check",
					label: "Routine Check",
					value: "RoutineCheck",
					isSelected: selectedReason === "RoutineCheck",
				},
			],
			onChange: (value: string) => setSelectedReason(value),
		},
	];

	const columns: Array<ColumnType<StaffVerifyResponse>> = [
		{
			header: "Date & Time",
			accessor: "createdAt",
			render: (record: StaffVerifyResponse) => <span>{formatTimestamp(record.createdAt ?? "")}</span>,
		},
		{
			accessor: "user",
			header: "Name of Customer",
			render: (record: StaffVerifyResponse) => <span>{record.customerName}</span>,
		},
		{
			accessor: "address",
			header: "Address",
			render: (record: StaffVerifyResponse) => <span>{record.customerAddress}</span>,
		},
		{
			accessor: "staffId",
			header: "Staff Id",
			render: (record: StaffVerifyResponse) => <>{record.staffId ? <span>{record.staffId}</span> : <div className={styles.notAvailableContainer}>NA</div>}</>,
		},
		{
			accessor: "staffMember",
			header: "Staff Member",
			render: (record: StaffVerifyResponse) => (
				<>{record.staffInfo ? <span>{record.staffInfo.name}</span> : <div className={styles.notAvailableContainer}>NA</div>}</>
			),
		},
		{
			accessor: "reasonForVisit",
			header: "Reason for Visit",
			render: (record: StaffVerifyResponse) => (
				<>{record.reason ? <span>{record.reason.name}</span> : <div className={styles.notAvailableContainer}>NA</div>}</>
			),
		},
	];

	// Header actions component
	const headerActions = (
		<>
			<SearchInput initialValue={searchTerm} onSearch={(value: string) => setSearchTerm(value)} placeholder="Search by customer / staff..." />
			<Button leftIcon={<FilterIcon2 className={styles.icon} />} text="Add Filter" variant="outline" type="button" onClick={handleOpenFilter} />
		</>
	);

	useEffect(() => {
		const params: StaffVerifyParams = {
			page: Page,
			pageSize: PageSize,
			searchPhrase: debouncedSearchTerm,
			from: appliedFilters.DateFrom,
			to: appliedFilters.DateTo,
			reasonIds: selectedReason ? [selectedReason] : undefined,
		};

		getStaffVerifyQuery(params);
	}, [Page, PageSize, appliedFilters.DateFrom, appliedFilters.DateTo, debouncedSearchTerm, selectedReason, getStaffVerifyQuery]);

	return (
		<>
			<div>
				<Filter
					filterItems={filterItems}
					onApplyFilter={handleApplyFilter}
					onResetFilter={handleResetFilter}
					onClose={handleCloseFilter}
					isOpen={isFilterOpen}
				/>
				<DataTable<StaffVerifyResponse>
					data={data?.items ?? []}
					loading={isLoading}
					isUpdating={isFetching}
					columns={columns}
					headerActions={headerActions}
					emptyComponent={<EmptyState icon={<ActivityLogActiveIcon />} title="No activity logs..." />}
				/>
				<TablePagination
					isLoading={isLoading}
					totalItems={data?.count ?? 0}
					itemsPerPage={PageSize}
					currentPage={data?.pageIndex ?? Page}
					onPageChange={setPage}
				/>
			</div>
		</>
	);
};

export default LogSearchTable;
